/**
 * AI mock data — phục vụ AI Hub (Tier 1), AI Co-pilot (Tier 2),
 * NLP Assistant (Tier 3) và Data Governance AI (Tier 4).
 */

export type AIModelType =
  | 'route-optimizer'
  | 'conflict-detector'
  | 'weather-nowcast'
  | 'target-classifier'
  | 'threat-scorer'
  | 'cargo-planner'
  | 'camera-anomaly'
  | 'satellite-anomaly'
  | 'sigmet-generator'
  | 'wind-corrector'
  | 'weapon-suggester'
  | 'pilot-workload'
  | 'nlp-assistant'
  | 'ocr-form'
  | 'entity-resolver'
  | 'data-quality'
  | 'anomaly-detector'

export type AIClearanceLevel = 'unclassified' | 'restricted' | 'confidential' | 'secret'

export type AIModelStatus = 'deployed' | 'staging' | 'training' | 'deprecated'

export interface AIModel {
  id: string
  code: string
  name: string
  type: AIModelType
  module: string
  version: string
  status: AIModelStatus
  accuracy: number
  precision: number
  recall: number
  f1: number
  inferenceMs: number
  clearance: AIClearanceLevel
  lastTrainedAt: string
  trainingDatasetSize: number
  description: string
  edgeCapable: boolean
}

export const AI_MODELS: AIModel[] = [
  {
    id: 'mdl-001',
    code: 'RT-OPT-V3',
    name: 'Route Optimizer (đường bay)',
    type: 'route-optimizer',
    module: 'ke-hoach-bay',
    version: '3.2.1',
    status: 'deployed',
    accuracy: 0.942,
    precision: 0.938,
    recall: 0.929,
    f1: 0.933,
    inferenceMs: 184,
    clearance: 'confidential',
    lastTrainedAt: '2026-04-22',
    trainingDatasetSize: 18420,
    description: 'Đề xuất tuyến tối ưu (fuel/time/weather/threat) cho kế hoạch bay nhiều máy bay.',
    edgeCapable: true,
  },
  {
    id: 'mdl-002',
    code: 'CF-DET-V2',
    name: 'Conflict Detector (giãn cách bay)',
    type: 'conflict-detector',
    module: 'ke-hoach-bay',
    version: '2.4.0',
    status: 'deployed',
    accuracy: 0.971,
    precision: 0.964,
    recall: 0.978,
    f1: 0.971,
    inferenceMs: 92,
    clearance: 'confidential',
    lastTrainedAt: '2026-05-03',
    trainingDatasetSize: 24100,
    description: 'Phát hiện xung đột giãn cách thời gian/không gian giữa các phiên bay.',
    edgeCapable: true,
  },
  {
    id: 'mdl-003',
    code: 'WX-NOW-V4',
    name: 'Weather Nowcast (0-6h)',
    type: 'weather-nowcast',
    module: 'khi-tuong',
    version: '4.1.0',
    status: 'deployed',
    accuracy: 0.886,
    precision: 0.871,
    recall: 0.902,
    f1: 0.886,
    inferenceMs: 312,
    clearance: 'restricted',
    lastTrainedAt: '2026-05-08',
    trainingDatasetSize: 62500,
    description: 'Dự báo hyperlocal 0-6h: gió, mưa đối lưu, CB-cell, wind shear.',
    edgeCapable: false,
  },
  {
    id: 'mdl-004',
    code: 'TG-CLS-V5',
    name: 'Target Classifier (radar)',
    type: 'target-classifier',
    module: 'bao-dong',
    version: '5.0.3',
    status: 'deployed',
    accuracy: 0.957,
    precision: 0.952,
    recall: 0.961,
    f1: 0.957,
    inferenceMs: 64,
    clearance: 'secret',
    lastTrainedAt: '2026-04-18',
    trainingDatasetSize: 89200,
    description: 'Phân loại mục tiêu radar (aircraft / drone / missile / civil) — giảm false alarm.',
    edgeCapable: true,
  },
  {
    id: 'mdl-005',
    code: 'TH-SCR-V2',
    name: 'Threat Scorer',
    type: 'threat-scorer',
    module: 'tac-chien',
    version: '2.1.0',
    status: 'deployed',
    accuracy: 0.918,
    precision: 0.908,
    recall: 0.928,
    f1: 0.918,
    inferenceMs: 128,
    clearance: 'secret',
    lastTrainedAt: '2026-04-30',
    trainingDatasetSize: 12800,
    description: 'Chấm điểm mức độ đe doạ từng track radar — ưu tiên xử lý.',
    edgeCapable: true,
  },
  {
    id: 'mdl-006',
    code: 'CG-PLN-V1',
    name: 'Cargo Load Planner',
    type: 'cargo-planner',
    module: 'van-tai',
    version: '1.5.2',
    status: 'deployed',
    accuracy: 0.894,
    precision: 0.886,
    recall: 0.901,
    f1: 0.893,
    inferenceMs: 245,
    clearance: 'restricted',
    lastTrainedAt: '2026-04-12',
    trainingDatasetSize: 8200,
    description: 'Tối ưu xếp hàng theo CG, weight limit, ưu tiên nhiệm vụ.',
    edgeCapable: false,
  },
  {
    id: 'mdl-007',
    code: 'CM-ANO-V2',
    name: 'Camera Anomaly Detection',
    type: 'camera-anomaly',
    module: 'van-tai',
    version: '2.0.1',
    status: 'deployed',
    accuracy: 0.923,
    precision: 0.914,
    recall: 0.932,
    f1: 0.923,
    inferenceMs: 156,
    clearance: 'restricted',
    lastTrainedAt: '2026-04-25',
    trainingDatasetSize: 34500,
    description: 'Phát hiện bất thường trên camera giám sát ga hàng không quân sự.',
    edgeCapable: true,
  },
  {
    id: 'mdl-008',
    code: 'SAT-ANO-V1',
    name: 'Satellite Anomaly',
    type: 'satellite-anomaly',
    module: 'khi-tuong',
    version: '1.3.0',
    status: 'staging',
    accuracy: 0.852,
    precision: 0.841,
    recall: 0.867,
    f1: 0.854,
    inferenceMs: 412,
    clearance: 'restricted',
    lastTrainedAt: '2026-05-09',
    trainingDatasetSize: 15600,
    description: 'Phát hiện front lạnh, bão lốc xoáy trên ảnh vệ tinh đa kênh.',
    edgeCapable: false,
  },
  {
    id: 'mdl-009',
    code: 'WD-CORR-V1',
    name: 'Wind Drift Corrector',
    type: 'wind-corrector',
    module: 'dan-duong',
    version: '1.2.4',
    status: 'deployed',
    accuracy: 0.961,
    precision: 0.958,
    recall: 0.965,
    f1: 0.961,
    inferenceMs: 38,
    clearance: 'restricted',
    lastTrainedAt: '2026-04-20',
    trainingDatasetSize: 28400,
    description: 'Tính toán hiệu chỉnh hướng/tốc độ do gió đẩy theo realtime.',
    edgeCapable: true,
  },
  {
    id: 'mdl-010',
    code: 'WP-SUG-V2',
    name: 'Weapon Suggester',
    type: 'weapon-suggester',
    module: 'dan-duong',
    version: '2.0.0',
    status: 'deployed',
    accuracy: 0.889,
    precision: 0.872,
    recall: 0.908,
    f1: 0.890,
    inferenceMs: 76,
    clearance: 'secret',
    lastTrainedAt: '2026-04-15',
    trainingDatasetSize: 6400,
    description: 'Đề xuất phương án vũ khí dựa target type, range, mission profile.',
    edgeCapable: true,
  },
  {
    id: 'mdl-011',
    code: 'PL-WKL-V1',
    name: 'Pilot Workload Balancer',
    type: 'pilot-workload',
    module: 'ke-hoach-bay',
    version: '1.4.1',
    status: 'deployed',
    accuracy: 0.911,
    precision: 0.903,
    recall: 0.919,
    f1: 0.911,
    inferenceMs: 142,
    clearance: 'restricted',
    lastTrainedAt: '2026-04-28',
    trainingDatasetSize: 14800,
    description: 'Cân bằng workload phi công theo giờ bay, mệt mỏi, training plan.',
    edgeCapable: false,
  },
  {
    id: 'mdl-012',
    code: 'NLP-ASR-V3',
    name: 'NLP Assistant (Trợ lý hội thoại)',
    type: 'nlp-assistant',
    module: 'global',
    version: '3.1.2',
    status: 'deployed',
    accuracy: 0.876,
    precision: 0.864,
    recall: 0.889,
    f1: 0.876,
    inferenceMs: 624,
    clearance: 'confidential',
    lastTrainedAt: '2026-05-05',
    trainingDatasetSize: 124000,
    description: 'Truy vấn dữ liệu tự nhiên, tra cứu quy chế, hỗ trợ vận hành 5 cấp SCH.',
    edgeCapable: false,
  },
  {
    id: 'mdl-013',
    code: 'OCR-FRM-V2',
    name: 'OCR Form Extractor',
    type: 'ocr-form',
    module: 'danh-muc',
    version: '2.2.0',
    status: 'deployed',
    accuracy: 0.943,
    precision: 0.939,
    recall: 0.948,
    f1: 0.943,
    inferenceMs: 892,
    clearance: 'restricted',
    lastTrainedAt: '2026-04-10',
    trainingDatasetSize: 22300,
    description: 'Trích xuất biểu mẫu giấy → field structured (giải quyết dữ liệu phân tán).',
    edgeCapable: false,
  },
  {
    id: 'mdl-014',
    code: 'ER-RES-V1',
    name: 'Entity Resolver',
    type: 'entity-resolver',
    module: 'danh-muc',
    version: '1.6.0',
    status: 'deployed',
    accuracy: 0.928,
    precision: 0.921,
    recall: 0.936,
    f1: 0.928,
    inferenceMs: 218,
    clearance: 'confidential',
    lastTrainedAt: '2026-04-08',
    trainingDatasetSize: 41200,
    description: 'Match thực thể trùng (đơn vị, phi công, máy bay) qua nhiều cách viết.',
    edgeCapable: true,
  },
  {
    id: 'mdl-015',
    code: 'DQ-SCR-V1',
    name: 'Data Quality Scorer',
    type: 'data-quality',
    module: 'danh-muc',
    version: '1.3.0',
    status: 'deployed',
    accuracy: 0.954,
    precision: 0.949,
    recall: 0.96,
    f1: 0.954,
    inferenceMs: 188,
    clearance: 'restricted',
    lastTrainedAt: '2026-04-29',
    trainingDatasetSize: 56000,
    description: 'Chấm điểm completeness / accuracy / freshness / consistency cho từng dataset.',
    edgeCapable: true,
  },
  {
    id: 'mdl-016',
    code: 'AN-DET-V2',
    name: 'Anomaly Detector (operational)',
    type: 'anomaly-detector',
    module: 'global',
    version: '2.5.1',
    status: 'deployed',
    accuracy: 0.901,
    precision: 0.892,
    recall: 0.913,
    f1: 0.902,
    inferenceMs: 196,
    clearance: 'confidential',
    lastTrainedAt: '2026-05-02',
    trainingDatasetSize: 78600,
    description: 'Phát hiện bất thường trong log bay, log bảo dưỡng, log truy cập hệ thống.',
    edgeCapable: true,
  },
  {
    id: 'mdl-017',
    code: 'SIG-GEN-V1',
    name: 'SIGMET Auto-Generator',
    type: 'sigmet-generator',
    module: 'khi-tuong',
    version: '1.1.0',
    status: 'staging',
    accuracy: 0.832,
    precision: 0.821,
    recall: 0.848,
    f1: 0.834,
    inferenceMs: 458,
    clearance: 'restricted',
    lastTrainedAt: '2026-05-10',
    trainingDatasetSize: 9400,
    description: 'Tự động sinh thông báo SIGMET khi phát hiện hiện tượng nguy hiểm.',
    edgeCapable: false,
  },
]

export const AI_MODELS_BY_ID = Object.fromEntries(AI_MODELS.map((m) => [m.id, m]))

export const CLEARANCE_LABEL: Record<AIClearanceLevel, string> = {
  unclassified: 'Không mật',
  restricted: 'Hạn chế',
  confidential: 'Mật',
  secret: 'Tối mật',
}

export const CLEARANCE_COLOR: Record<AIClearanceLevel, string> = {
  unclassified: 'default',
  restricted: 'blue',
  confidential: 'orange',
  secret: 'red',
}

export const MODEL_STATUS_LABEL: Record<AIModelStatus, string> = {
  deployed: 'Đã triển khai',
  staging: 'Staging',
  training: 'Đang huấn luyện',
  deprecated: 'Ngưng dùng',
}

export const MODEL_STATUS_COLOR: Record<AIModelStatus, string> = {
  deployed: 'success',
  staging: 'processing',
  training: 'warning',
  deprecated: 'default',
}

/* ===================== AUDIT LOG ===================== */

export type AIActionType = 'view' | 'apply' | 'reject' | 'override' | 'feedback'

export interface AIAuditEntry {
  id: string
  timestamp: string
  modelId: string
  modelCode: string
  action: AIActionType
  entityType: string
  entityId: string
  userName: string
  userRank: string
  unitLevel: 'quan-chung' | 'su-doan' | 'trung-doan' | 'tieu-doan' | 'dai-doi'
  unitCode: string
  confidence: number
  summary: string
  reason?: string
}

const AUDIT_ACTIONS_RAW: Omit<AIAuditEntry, 'id' | 'timestamp'>[] = [
  {
    modelId: 'mdl-001',
    modelCode: 'RT-OPT-V3',
    action: 'apply',
    entityType: 'flight-plan',
    entityId: 'fp-12',
    userName: 'Nguyễn Văn Hùng',
    userRank: 'Thiếu tá',
    unitLevel: 'trung-doan',
    unitCode: 'E927',
    confidence: 0.91,
    summary: 'Áp dụng tuyến tối ưu — tiết kiệm 12% fuel',
    reason: 'Lệch chỉ 3 NM khỏi tuyến gốc, an toàn',
  },
  {
    modelId: 'mdl-002',
    modelCode: 'CF-DET-V2',
    action: 'apply',
    entityType: 'flight-plan',
    entityId: 'fp-15',
    userName: 'Lê Quang Minh',
    userRank: 'Trung tá',
    unitLevel: 'su-doan',
    unitCode: 'F371',
    confidence: 0.97,
    summary: 'Dời cất cánh +15 phút — tránh giãn cách với fp-09',
  },
  {
    modelId: 'mdl-004',
    modelCode: 'TG-CLS-V5',
    action: 'override',
    entityType: 'radar-target',
    entityId: 'trk-1023',
    userName: 'Phạm Văn Đức',
    userRank: 'Đại uý',
    unitLevel: 'dai-doi',
    unitCode: 'D1-E921',
    confidence: 0.78,
    summary: 'AI dự đoán: drone — Override sang: civil',
    reason: 'Track phù hợp lịch trình AirAsia QZ123',
  },
  {
    modelId: 'mdl-005',
    modelCode: 'TH-SCR-V2',
    action: 'view',
    entityType: 'radar-target',
    entityId: 'trk-1041',
    userName: 'Trần Hữu Long',
    userRank: 'Thượng uý',
    unitLevel: 'dai-doi',
    unitCode: 'D2-E921',
    confidence: 0.89,
    summary: 'Threat score 0.89 — High',
  },
  {
    modelId: 'mdl-003',
    modelCode: 'WX-NOW-V4',
    action: 'apply',
    entityType: 'flight-plan',
    entityId: 'fp-22',
    userName: 'Hoàng Thị Mai',
    userRank: 'Đại uý',
    unitLevel: 'trung-doan',
    unitCode: 'E910',
    confidence: 0.84,
    summary: 'Hoãn cất cánh 45 phút — CB-cell đến trong 30 phút',
  },
  {
    modelId: 'mdl-011',
    modelCode: 'PL-WKL-V1',
    action: 'apply',
    entityType: 'pilot-roster',
    entityId: 'pl-2026-W19',
    userName: 'Nguyễn Văn Hùng',
    userRank: 'Thiếu tá',
    unitLevel: 'trung-doan',
    unitCode: 'E927',
    confidence: 0.93,
    summary: 'Re-balance 4 phi công — workload chênh max 3.2h',
  },
  {
    modelId: 'mdl-010',
    modelCode: 'WP-SUG-V2',
    action: 'reject',
    entityType: 'mission',
    entityId: 'msn-088',
    userName: 'Bùi Tuấn Anh',
    userRank: 'Trung tá',
    unitLevel: 'su-doan',
    unitCode: 'F371',
    confidence: 0.71,
    summary: 'AI đề xuất R-77 — Reject',
    reason: 'Target ngoài tầm engagement, dùng GBU phù hợp hơn',
  },
  {
    modelId: 'mdl-012',
    modelCode: 'NLP-ASR-V3',
    action: 'view',
    entityType: 'query',
    entityId: 'q-2031',
    userName: 'Đào Mạnh Hà',
    userRank: 'Đại tá',
    unitLevel: 'quan-chung',
    unitCode: 'QC PK-KQ',
    confidence: 0.94,
    summary: 'Query: "Tuần này phiên bay nào chở vũ khí?"',
  },
  {
    modelId: 'mdl-014',
    modelCode: 'ER-RES-V1',
    action: 'apply',
    entityType: 'master-data',
    entityId: 'merge-pilot-018',
    userName: 'Phạm Thanh Tùng',
    userRank: 'Thiếu tá',
    unitLevel: 'quan-chung',
    unitCode: 'QC PK-KQ',
    confidence: 0.96,
    summary: 'Merge 3 bản ghi phi công trùng — Nguyễn Văn A',
  },
  {
    modelId: 'mdl-013',
    modelCode: 'OCR-FRM-V2',
    action: 'apply',
    entityType: 'master-data',
    entityId: 'ocr-batch-2026-05-10',
    userName: 'Vũ Đức Tài',
    userRank: 'Thượng uý',
    unitLevel: 'tieu-doan',
    unitCode: 'D-E927',
    confidence: 0.92,
    summary: 'Trích xuất 24 biểu mẫu giấy — 22 valid, 2 cần review',
  },
  {
    modelId: 'mdl-009',
    modelCode: 'WD-CORR-V1',
    action: 'apply',
    entityType: 'flight-plan',
    entityId: 'fp-08',
    userName: 'Lê Văn Tú',
    userRank: 'Đại uý',
    unitLevel: 'trung-doan',
    unitCode: 'E927',
    confidence: 0.95,
    summary: 'Hiệu chỉnh +8° heading — gió cross 18kt từ 270°',
  },
  {
    modelId: 'mdl-016',
    modelCode: 'AN-DET-V2',
    action: 'view',
    entityType: 'access-log',
    entityId: 'al-2026-05-11-088',
    userName: 'Nguyễn Hồng Quân',
    userRank: 'Trung tá',
    unitLevel: 'quan-chung',
    unitCode: 'QC PK-KQ',
    confidence: 0.88,
    summary: 'Phát hiện truy cập ngoài giờ — review thủ công',
  },
]

export const AI_AUDIT_LOG: AIAuditEntry[] = AUDIT_ACTIONS_RAW.map((a, i) => ({
  ...a,
  id: `au-${(i + 1).toString().padStart(4, '0')}`,
  timestamp: new Date(2026, 4, 11 - Math.floor(i / 3), 10 + (i % 12), (i * 7) % 60).toISOString(),
}))

export const AI_ACTION_LABEL: Record<AIActionType, string> = {
  view: 'Xem',
  apply: 'Áp dụng',
  reject: 'Bỏ qua',
  override: 'Ghi đè',
  feedback: 'Phản hồi',
}

export const AI_ACTION_COLOR: Record<AIActionType, string> = {
  view: 'default',
  apply: 'success',
  reject: 'warning',
  override: 'orange',
  feedback: 'blue',
}

/* ===================== RBAC ===================== */

export interface AIPermission {
  modelId: string
  roleLevel: 'quan-chung' | 'su-doan' | 'trung-doan' | 'tieu-doan' | 'dai-doi'
  canView: boolean
  canApply: boolean
  canOverride: boolean
  canRetrain: boolean
}

const ROLE_LEVELS: AIPermission['roleLevel'][] = [
  'quan-chung',
  'su-doan',
  'trung-doan',
  'tieu-doan',
  'dai-doi',
]

export const AI_PERMISSIONS: AIPermission[] = AI_MODELS.flatMap((m) =>
  ROLE_LEVELS.map((lvl) => {
    const isSecret = m.clearance === 'secret'
    const isTopLevel = lvl === 'quan-chung' || lvl === 'su-doan'
    const isMidLevel = lvl === 'trung-doan'
    return {
      modelId: m.id,
      roleLevel: lvl,
      canView: !isSecret || isTopLevel || isMidLevel,
      canApply: !isSecret ? isTopLevel || isMidLevel : isTopLevel,
      canOverride: isTopLevel,
      canRetrain: lvl === 'quan-chung',
    } as AIPermission
  }),
)

/* ===================== EDGE NODES ===================== */

export interface AIEdgeNode {
  id: string
  code: string
  name: string
  level: 'quan-chung' | 'su-doan' | 'trung-doan'
  region: 'north' | 'central' | 'south'
  status: 'online' | 'offline' | 'syncing' | 'degraded'
  modelsDeployed: number
  lastSync: string
  pendingSync: number
  cpuUsage: number
  gpuUsage: number
  diskUsage: number
}

export const AI_EDGE_NODES: AIEdgeNode[] = [
  {
    id: 'edg-001',
    code: 'EDG-QC01',
    name: 'SCH Quân chủng',
    level: 'quan-chung',
    region: 'north',
    status: 'online',
    modelsDeployed: 14,
    lastSync: '2026-05-11T08:42:00',
    pendingSync: 0,
    cpuUsage: 42,
    gpuUsage: 28,
    diskUsage: 56,
  },
  {
    id: 'edg-002',
    code: 'EDG-F371',
    name: 'SCH Sư đoàn 371',
    level: 'su-doan',
    region: 'north',
    status: 'online',
    modelsDeployed: 11,
    lastSync: '2026-05-11T08:38:00',
    pendingSync: 2,
    cpuUsage: 38,
    gpuUsage: 22,
    diskUsage: 48,
  },
  {
    id: 'edg-003',
    code: 'EDG-F372',
    name: 'SCH Sư đoàn 372',
    level: 'su-doan',
    region: 'central',
    status: 'syncing',
    modelsDeployed: 11,
    lastSync: '2026-05-11T07:20:00',
    pendingSync: 12,
    cpuUsage: 64,
    gpuUsage: 51,
    diskUsage: 52,
  },
  {
    id: 'edg-004',
    code: 'EDG-F370',
    name: 'SCH Sư đoàn 370',
    level: 'su-doan',
    region: 'south',
    status: 'online',
    modelsDeployed: 11,
    lastSync: '2026-05-11T08:35:00',
    pendingSync: 0,
    cpuUsage: 32,
    gpuUsage: 18,
    diskUsage: 41,
  },
  {
    id: 'edg-005',
    code: 'EDG-E927',
    name: 'SCH Trung đoàn 927',
    level: 'trung-doan',
    region: 'north',
    status: 'online',
    modelsDeployed: 8,
    lastSync: '2026-05-11T08:40:00',
    pendingSync: 1,
    cpuUsage: 28,
    gpuUsage: 15,
    diskUsage: 38,
  },
  {
    id: 'edg-006',
    code: 'EDG-E921',
    name: 'SCH Trung đoàn 921',
    level: 'trung-doan',
    region: 'north',
    status: 'degraded',
    modelsDeployed: 8,
    lastSync: '2026-05-11T05:12:00',
    pendingSync: 28,
    cpuUsage: 88,
    gpuUsage: 81,
    diskUsage: 92,
  },
  {
    id: 'edg-007',
    code: 'EDG-E910',
    name: 'SCH Trung đoàn 910',
    level: 'trung-doan',
    region: 'central',
    status: 'online',
    modelsDeployed: 8,
    lastSync: '2026-05-11T08:32:00',
    pendingSync: 3,
    cpuUsage: 35,
    gpuUsage: 20,
    diskUsage: 44,
  },
  {
    id: 'edg-008',
    code: 'EDG-E935',
    name: 'SCH Trung đoàn 935',
    level: 'trung-doan',
    region: 'south',
    status: 'offline',
    modelsDeployed: 8,
    lastSync: '2026-05-10T22:18:00',
    pendingSync: 47,
    cpuUsage: 0,
    gpuUsage: 0,
    diskUsage: 51,
  },
]

export const EDGE_STATUS_LABEL: Record<AIEdgeNode['status'], string> = {
  online: 'Online',
  offline: 'Offline',
  syncing: 'Đang đồng bộ',
  degraded: 'Suy giảm',
}

export const EDGE_STATUS_COLOR: Record<AIEdgeNode['status'], string> = {
  online: 'success',
  offline: 'error',
  syncing: 'processing',
  degraded: 'warning',
}

/* ===================== DRIFT MONITORING ===================== */

export interface AIDriftPoint {
  date: string
  accuracy: number
  drift: number
}

export function generateDriftSeries(modelId: string, days = 30): AIDriftPoint[] {
  const model = AI_MODELS_BY_ID[modelId]
  if (!model) return []
  const base = model.accuracy
  // deterministic from modelId
  let seed = 0
  for (let i = 0; i < modelId.length; i++) seed = seed * 31 + modelId.charCodeAt(i)
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    return seed / 0x7fffffff
  }
  const points: AIDriftPoint[] = []
  for (let i = 0; i < days; i++) {
    const date = new Date(2026, 4, 11)
    date.setDate(date.getDate() - (days - 1 - i))
    const noise = (rand() - 0.5) * 0.04
    const driftEffect = (i / days) * 0.02 * (rand() < 0.4 ? -1 : 1)
    const acc = Math.max(0.7, Math.min(0.99, base + noise + driftEffect))
    points.push({
      date: date.toISOString().split('T')[0],
      accuracy: +acc.toFixed(4),
      drift: +Math.abs(base - acc).toFixed(4),
    })
  }
  return points
}

/* ===================== AI SUGGESTIONS (entity-level) ===================== */

export type AISuggestionImpact = 'low' | 'medium' | 'high'

export interface AISuggestion {
  id: string
  modelId: string
  modelCode: string
  modelName: string
  entityType: string
  entityId: string
  title: string
  summary: string
  rationale: string[]
  confidence: number
  impact: AISuggestionImpact
  metrics?: { label: string; value: string; delta?: string }[]
  alternatives?: { label: string; confidence: number }[]
  generatedAt: string
  clearance: AIClearanceLevel
}

export const IMPACT_COLOR: Record<AISuggestionImpact, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
}

export const IMPACT_LABEL: Record<AISuggestionImpact, string> = {
  low: 'Thấp',
  medium: 'Trung bình',
  high: 'Cao',
}

function seedFrom(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = h * 31 + s.charCodeAt(i)
  return Math.abs(h)
}

export function getSuggestionsForEntity(
  entityType: string,
  entityId: string,
): AISuggestion[] {
  const seed = seedFrom(entityType + entityId)
  const ctx = SUGGESTION_TEMPLATES[entityType] || []
  if (ctx.length === 0) return []
  const count = Math.max(1, 1 + (seed % 3))
  const out: AISuggestion[] = []
  for (let i = 0; i < count && i < ctx.length; i++) {
    const tmpl = ctx[(seed + i) % ctx.length]
    const conf = 0.72 + ((seed >> i) % 25) / 100
    out.push({
      id: `sug-${entityId}-${i}`,
      modelId: tmpl.modelId,
      modelCode: AI_MODELS_BY_ID[tmpl.modelId]?.code || tmpl.modelId,
      modelName: AI_MODELS_BY_ID[tmpl.modelId]?.name || '',
      entityType,
      entityId,
      title: tmpl.title,
      summary: tmpl.summary,
      rationale: tmpl.rationale,
      confidence: +conf.toFixed(3),
      impact: tmpl.impact,
      metrics: tmpl.metrics,
      alternatives: tmpl.alternatives,
      generatedAt: new Date().toISOString(),
      clearance: AI_MODELS_BY_ID[tmpl.modelId]?.clearance || 'restricted',
    })
  }
  return out
}

type SuggestionTemplate = Omit<
  AISuggestion,
  'id' | 'entityType' | 'entityId' | 'modelCode' | 'modelName' | 'generatedAt' | 'clearance' | 'confidence'
>

const SUGGESTION_TEMPLATES: Record<string, SuggestionTemplate[]> = {
  'flight-plan': [
    {
      modelId: 'mdl-001',
      title: 'Tối ưu tuyến — tiết kiệm fuel & né wx',
      summary: 'Đề xuất dịch waypoint #3 sang phải 4NM, tránh CB-cell và giảm fuel ~9%.',
      rationale: [
        'Heading hiện tại đi qua vùng convective phát triển trong 45 phút tới',
        'Tuyến mới chỉ dài thêm 6.2km nhưng giảm headwind đáng kể',
        'Khu vực mới không xung đột không vực hoạt động khác',
      ],
      impact: 'medium',
      metrics: [
        { label: 'Tiết kiệm fuel', value: '~9%', delta: '-180kg' },
        { label: 'Thời gian', value: '-2 phút' },
        { label: 'Lệch tuyến', value: '+6.2km' },
      ],
      alternatives: [
        { label: 'Giữ tuyến gốc + lùi cất cánh 30 phút', confidence: 0.81 },
        { label: 'Đổi mức bay FL280 → FL310', confidence: 0.74 },
      ],
    },
    {
      modelId: 'mdl-002',
      title: 'Cảnh báo giãn cách bay',
      summary: 'Phát hiện giao cắt với phiên bay khác cùng không vực ATC-3 lúc 09:45.',
      rationale: [
        'Phiên fp-09 sẽ qua waypoint NB→KN lúc 09:43-09:48',
        'Mức bay chênh nhau chỉ 500ft, khoảng cách min 8NM',
        'Theo quy tắc PK-KQ: vertical sep >1000ft hoặc horizontal >10NM',
      ],
      impact: 'high',
      metrics: [
        { label: 'Vertical sep', value: '500ft', delta: 'Thiếu 500ft' },
        { label: 'Horizontal sep', value: '8NM', delta: 'Thiếu 2NM' },
      ],
      alternatives: [
        { label: 'Lùi cất cánh +15 phút', confidence: 0.94 },
        { label: 'Tăng FL lên 290', confidence: 0.88 },
      ],
    },
    {
      modelId: 'mdl-011',
      title: 'Cân bằng workload phi công',
      summary: 'Đề xuất hoán đổi 1 phi công — workload tuần chênh đang quá cao.',
      rationale: [
        'Cơ trưởng Nguyễn Văn A đã bay 22.5h tuần này (max khuyến nghị 20h)',
        'Phi công thay thế Trần Văn B mới bay 8h, đủ điều kiện',
        'Không ảnh hưởng lịch huấn luyện đã duyệt',
      ],
      impact: 'medium',
      metrics: [
        { label: 'Giảm fatigue risk', value: '-32%' },
      ],
    },
  ],
  'radar-target': [
    {
      modelId: 'mdl-004',
      title: 'Phân loại: Civil (AirAsia QZ123)',
      summary: 'AI nhận diện đây là chuyến bay dân dụng theo lịch trình.',
      rationale: [
        'Track signature phù hợp Airbus A320',
        'Flight schedule match: QZ123 KUL→HAN ETA 09:42',
        'Squawk code 4351 = Civil ATC',
      ],
      impact: 'low',
      metrics: [
        { label: 'Match score', value: '94%' },
      ],
      alternatives: [
        { label: 'Drone (large class)', confidence: 0.12 },
        { label: 'Military transport', confidence: 0.08 },
      ],
    },
    {
      modelId: 'mdl-005',
      title: 'Threat score: HIGH (0.87)',
      summary: 'Mục tiêu có pattern bất thường — yêu cầu xác minh cấp Sư đoàn.',
      rationale: [
        'Track không có flight plan đăng ký',
        'Vận tốc 480kt và độ cao 18000ft — fighter profile',
        'Hướng tiếp cận không vực hạn chế R-12',
      ],
      impact: 'high',
      metrics: [
        { label: 'Threat score', value: '0.87' },
        { label: 'ETA biên giới', value: '~12 phút' },
      ],
    },
  ],
  'weather-region': [
    {
      modelId: 'mdl-003',
      title: 'Nowcast: CB-cell phát triển 2h tới',
      summary: 'AI dự báo cụm mưa đối lưu mạnh sẽ vào khu vực bay Yên Bái trong 80-110 phút.',
      rationale: [
        'Phân tích radar reflectivity + satellite IR',
        'Wind shear đang tăng — môi trường convective favorable',
        'Tương tự pattern 12/04/2026 (CB-cell 50dBz)',
      ],
      impact: 'high',
      metrics: [
        { label: 'Confidence', value: '84%' },
        { label: 'ETA', value: '90 phút' },
        { label: 'Mức', value: 'TS / +RA / TURB-MOD' },
      ],
    },
  ],
  'cargo-load': [
    {
      modelId: 'mdl-006',
      title: 'Tối ưu xếp hàng — CG trong limit',
      summary: 'Phương án xếp 12 pallet đạt MAC 28.3% (limit 22-32%), tận dụng 94% trọng tải.',
      rationale: [
        'Pallet nặng ưu tiên giữa thân (FS 480-520)',
        'Cargo chuyên dụng (mảnh, dễ vỡ) đặt sau',
        'Đảm bảo emergency egress',
      ],
      impact: 'medium',
      metrics: [
        { label: 'Trọng tải sử dụng', value: '94%' },
        { label: 'MAC', value: '28.3%' },
        { label: 'Số pallet', value: '12' },
      ],
    },
  ],
  alarm: [
    {
      modelId: 'mdl-016',
      title: 'Anomaly: 3 truy cập ngoài giờ',
      summary: 'Phát hiện cluster truy cập bất thường lúc 02:14-02:17 từ IP 10.20.x.x.',
      rationale: [
        'User không trong shift trực ban',
        'Cùng 1 user nhưng 3 IP khác nhau',
        'Tỉ lệ tương tự sự cố 2026-02-08',
      ],
      impact: 'high',
    },
  ],
  navigation: [
    {
      modelId: 'mdl-009',
      title: 'Hiệu chỉnh gió: +8° heading',
      summary: 'Wind 270°/18kt — cần bias heading +8° để giữ track ground.',
      rationale: [
        'Cross-wind component: 16.5kt từ trái',
        'Crab angle tính toán: 7.8°',
        'Áp dụng từ waypoint hiện tại đến điểm tới',
      ],
      impact: 'low',
      metrics: [
        { label: 'Bias heading', value: '+8°' },
        { label: 'Wind correction', value: '7.8°' },
      ],
    },
    {
      modelId: 'mdl-010',
      title: 'Phương án vũ khí: 2× R-77 + 2× R-73',
      summary: 'Cấu hình tối ưu cho mission patrol BVR + WVR.',
      rationale: [
        'Target profile: fighter-class 4-gen',
        'Engagement range expected: 30-80 NM',
        'Mix tối ưu cho both BVR và WVR scenarios',
      ],
      impact: 'high',
      metrics: [
        { label: 'BVR coverage', value: '2× R-77 (80NM)' },
        { label: 'WVR coverage', value: '2× R-73 (40NM)' },
      ],
      alternatives: [
        { label: '4× R-77 (pure BVR)', confidence: 0.71 },
        { label: '4× R-73 (close-in only)', confidence: 0.43 },
      ],
    },
  ],
}

/* ===================== NLP ASSISTANT — scripted ===================== */

export interface NLPSampleResponse {
  query: string
  answer: string
  source: string[]
  relatedActions?: { label: string; path: string }[]
  clearance: AIClearanceLevel
}

export const NLP_SAMPLES: NLPSampleResponse[] = [
  {
    query: 'Tuần này có phiên bay nào chở vũ khí?',
    answer:
      'Tuần này có **3 phiên bay** chở vũ khí: FP-08 (Su-30, R-77×4, T2 09:00), FP-15 (Su-22, GBU×2, T4 14:30), FP-22 (Su-30, R-73×2, T6 07:45). Tất cả đã được duyệt cấp Sư đoàn.',
    source: ['flight-plans', 'aircraft-loadout'],
    relatedActions: [
      { label: 'Xem chi tiết FP-08', path: '/ke-hoach-bay/plans/fp-08' },
      { label: 'Lọc theo combat-readiness', path: '/ke-hoach-bay/plans?purpose=combat-readiness' },
    ],
    clearance: 'confidential',
  },
  {
    query: 'Giãn cách bay F-16 với Su-30 là bao nhiêu?',
    answer:
      'Theo quy tắc giãn cách PK-KQ 2026: **vertical ≥1000ft**, **horizontal ≥10NM** đối với tiêm kích cùng cấp tốc độ. F-16 (Mach 2.0) và Su-30 (Mach 2.0) thuộc cùng nhóm fast-jet.',
    source: ['separation-rules', 'aircraft-catalog'],
    relatedActions: [
      { label: 'Xem quy tắc giãn cách', path: '/danh-muc/quy-tac-gian-cach' },
    ],
    clearance: 'restricted',
  },
  {
    query: 'Phi công nào sắp hết hạn giấy phép y tế?',
    answer:
      'Có **4 phi công** sắp hết hạn giấy phép y tế trong 30 ngày tới: Nguyễn Văn A (E927) — 15/05, Lê Minh B (E921) — 22/05, Trần Đức C (E910) — 28/05, Phạm Tuấn D (E935) — 03/06.',
    source: ['pilot-medical', 'flight-medical-certificates'],
    relatedActions: [
      { label: 'Lịch khám sức khoẻ', path: '/y-te' },
    ],
    clearance: 'confidential',
  },
  {
    query: 'Có target nào trong AOR-3 không?',
    answer:
      'Hiện tại có **2 target** trong AOR-3: TRK-1041 (military, threat 0.87, đang tracking), TRK-1023 (civil, AirAsia QZ123, friendly). Toàn bộ đã được phân loại tự động.',
    source: ['radar-targets', 'aor-zones'],
    relatedActions: [
      { label: 'Mở bản đồ tactical', path: '/bao-dong' },
    ],
    clearance: 'secret',
  },
  {
    query: 'Tình hình khí tượng sân Đà Nẵng?',
    answer:
      'Sân **Đà Nẵng (DAD)** hiện tại: gió 120°/8kt, visibility 8km, ceiling 3500ft (FEW), QNH 1012, RA-LIGHT. Flight category **MVFR**. Dự báo 6h: gió tăng nhẹ, mưa giông khả năng 40% sau 14:00.',
    source: ['weather-stations', 'weather-nowcast'],
    relatedActions: [
      { label: 'Bản đồ khí tượng', path: '/khi-tuong/map' },
    ],
    clearance: 'restricted',
  },
  {
    query: 'Máy bay nào cần bảo dưỡng trong tuần này?',
    answer:
      'Có **5 máy bay** cần bảo dưỡng tuần này: Su-30MK2 7805-01 (100h check, 12/05), Mi-17 7715-03 (50h check, 13/05), Su-22 7812-02 (annual, 14/05), L-39 7902-04 (50h, 15/05), Yak-130 7905-01 (100h, 16/05).',
    source: ['aircraft-catalog', 'maintenance-schedule'],
    clearance: 'restricted',
  },
]

/* ===================== DATA QUALITY (for Tier 4) ===================== */

export interface DataQualityScore {
  domain: string
  domainLabel: string
  completeness: number
  accuracy: number
  freshness: number
  consistency: number
  overall: number
  grade: 'A' | 'B' | 'C' | 'D' | 'E'
  totalRecords: number
  issues: number
  duplicates: number
  ownerUnit: string
}

function grade(score: number): DataQualityScore['grade'] {
  if (score >= 0.9) return 'A'
  if (score >= 0.8) return 'B'
  if (score >= 0.7) return 'C'
  if (score >= 0.6) return 'D'
  return 'E'
}

const DQ_RAW: Omit<DataQualityScore, 'overall' | 'grade'>[] = [
  {
    domain: 'pilots',
    domainLabel: 'Phi công',
    completeness: 0.96,
    accuracy: 0.94,
    freshness: 0.91,
    consistency: 0.93,
    totalRecords: 1842,
    issues: 28,
    duplicates: 6,
    ownerUnit: 'QC PK-KQ',
  },
  {
    domain: 'aircraft',
    domainLabel: 'Máy bay',
    completeness: 0.98,
    accuracy: 0.97,
    freshness: 0.95,
    consistency: 0.96,
    totalRecords: 248,
    issues: 4,
    duplicates: 0,
    ownerUnit: 'QC PK-KQ',
  },
  {
    domain: 'airports',
    domainLabel: 'Sân bay',
    completeness: 0.99,
    accuracy: 0.98,
    freshness: 0.99,
    consistency: 0.98,
    totalRecords: 86,
    issues: 1,
    duplicates: 0,
    ownerUnit: 'QC PK-KQ',
  },
  {
    domain: 'airspaces',
    domainLabel: 'Không vực',
    completeness: 0.92,
    accuracy: 0.89,
    freshness: 0.86,
    consistency: 0.84,
    totalRecords: 142,
    issues: 12,
    duplicates: 3,
    ownerUnit: 'QC PK-KQ',
  },
  {
    domain: 'weapons',
    domainLabel: 'Vũ khí, khí tài',
    completeness: 0.78,
    accuracy: 0.82,
    freshness: 0.71,
    consistency: 0.74,
    totalRecords: 3621,
    issues: 184,
    duplicates: 42,
    ownerUnit: 'Cục Hậu cần',
  },
  {
    domain: 'units',
    domainLabel: 'Đơn vị',
    completeness: 0.94,
    accuracy: 0.96,
    freshness: 0.88,
    consistency: 0.85,
    totalRecords: 412,
    issues: 18,
    duplicates: 8,
    ownerUnit: 'QC PK-KQ',
  },
  {
    domain: 'routes',
    domainLabel: 'Đường bay',
    completeness: 0.88,
    accuracy: 0.91,
    freshness: 0.79,
    consistency: 0.82,
    totalRecords: 156,
    issues: 22,
    duplicates: 5,
    ownerUnit: 'Cục Quản lý bay',
  },
  {
    domain: 'civil-coord',
    domainLabel: 'Hiệp đồng dân dụng',
    completeness: 0.71,
    accuracy: 0.76,
    freshness: 0.62,
    consistency: 0.68,
    totalRecords: 94,
    issues: 28,
    duplicates: 11,
    ownerUnit: 'Cục HKDD',
  },
]

export const DATA_QUALITY_SCORES: DataQualityScore[] = DQ_RAW.map((r) => {
  const overall =
    +(r.completeness * 0.3 + r.accuracy * 0.3 + r.freshness * 0.2 + r.consistency * 0.2).toFixed(3)
  return { ...r, overall, grade: grade(overall) }
})

export const GRADE_COLOR: Record<DataQualityScore['grade'], string> = {
  A: 'green',
  B: 'cyan',
  C: 'gold',
  D: 'orange',
  E: 'red',
}

/* ===================== ENTITY RESOLUTION CANDIDATES ===================== */

export interface EntityResolutionPair {
  id: string
  domain: string
  domainLabel: string
  records: { source: string; key: string; display: string }[]
  matchScore: number
  reason: string[]
  status: 'pending' | 'confirmed' | 'rejected'
}

export const ENTITY_RESOLUTION_PAIRS: EntityResolutionPair[] = [
  {
    id: 'er-001',
    domain: 'pilots',
    domainLabel: 'Phi công',
    records: [
      { source: 'E927 HR', key: 'P-927-018', display: 'Nguyễn Văn A · 1985 · Su-30' },
      { source: 'BQP Personnel', key: 'BQP-018442', display: 'Nguyen Van A · 12/08/1985 · Phi công Su-30' },
      { source: 'Excel C2-2025', key: 'PL-018', display: 'NV.A · Phi đội 2 · Su-30MK2' },
    ],
    matchScore: 0.96,
    reason: ['Năm sinh khớp', 'Loại máy bay khớp', 'Đơn vị khớp'],
    status: 'pending',
  },
  {
    id: 'er-002',
    domain: 'units',
    domainLabel: 'Đơn vị',
    records: [
      { source: 'Org chart', key: 'U-E927-D1', display: 'Phi đội 1 / Trung đoàn 927' },
      { source: 'Excel báo cáo', key: 'E927.PĐ1', display: 'PĐ.1 — E927' },
    ],
    matchScore: 0.93,
    reason: ['Code partial match', 'Parent unit khớp'],
    status: 'pending',
  },
  {
    id: 'er-003',
    domain: 'aircraft',
    domainLabel: 'Máy bay',
    records: [
      { source: 'Maintenance log', key: 'AC-7805-01', display: 'Su-30MK2 #7805-01' },
      { source: 'Flight log', key: '7805/01', display: 'Su-30MK2 7805/01' },
    ],
    matchScore: 0.98,
    reason: ['Tail number khớp', 'Loại máy bay khớp'],
    status: 'pending',
  },
  {
    id: 'er-004',
    domain: 'pilots',
    domainLabel: 'Phi công',
    records: [
      { source: 'E921', key: 'P-921-044', display: 'Lê Quốc Bảo · 1988 · Su-22' },
      { source: 'Y tế', key: 'MED-2811', display: 'L.Q.Bảo · 1988 · F371/E921' },
    ],
    matchScore: 0.89,
    reason: ['Tên viết tắt', 'Năm sinh khớp', 'Đơn vị khớp'],
    status: 'pending',
  },
]

/* ===================== OCR FORM SAMPLES ===================== */

export interface OCRSample {
  id: string
  formType: string
  uploadedAt: string
  uploadedBy: string
  unitCode: string
  pages: number
  fieldsExtracted: number
  fieldsTotal: number
  confidenceAvg: number
  status: 'pending-review' | 'approved' | 'needs-correction'
  flaggedFields: number
}

export const OCR_SAMPLES: OCRSample[] = [
  {
    id: 'ocr-001',
    formType: 'Báo cáo trực ban CB',
    uploadedAt: '2026-05-11T07:42:00',
    uploadedBy: 'Trần Văn Tùng',
    unitCode: 'D1-E927',
    pages: 2,
    fieldsExtracted: 28,
    fieldsTotal: 30,
    confidenceAvg: 0.94,
    status: 'pending-review',
    flaggedFields: 2,
  },
  {
    id: 'ocr-002',
    formType: 'Phiếu bay phi công',
    uploadedAt: '2026-05-11T08:15:00',
    uploadedBy: 'Nguyễn Tiến Đạt',
    unitCode: 'E921',
    pages: 1,
    fieldsExtracted: 18,
    fieldsTotal: 18,
    confidenceAvg: 0.97,
    status: 'approved',
    flaggedFields: 0,
  },
  {
    id: 'ocr-003',
    formType: 'Biên bản kiểm tra kỹ thuật',
    uploadedAt: '2026-05-10T15:22:00',
    uploadedBy: 'Lê Mạnh Cường',
    unitCode: 'F371',
    pages: 4,
    fieldsExtracted: 52,
    fieldsTotal: 64,
    confidenceAvg: 0.81,
    status: 'needs-correction',
    flaggedFields: 12,
  },
  {
    id: 'ocr-004',
    formType: 'Báo cáo khí tượng định kỳ',
    uploadedAt: '2026-05-10T08:00:00',
    uploadedBy: 'Phạm Văn Hùng',
    unitCode: 'E910',
    pages: 1,
    fieldsExtracted: 22,
    fieldsTotal: 22,
    confidenceAvg: 0.96,
    status: 'approved',
    flaggedFields: 0,
  },
]

/* ===================== AI KPI ===================== */

export const AI_KPIS = {
  totalModels: AI_MODELS.length,
  deployedModels: AI_MODELS.filter((m) => m.status === 'deployed').length,
  avgAccuracy: +(
    AI_MODELS.reduce((s, m) => s + m.accuracy, 0) / AI_MODELS.length
  ).toFixed(3),
  totalSuggestionsToday: 1248,
  appliedToday: 894,
  rejectedToday: 124,
  overriddenToday: 38,
  applyRate: 0.716,
  edgeNodesOnline: AI_EDGE_NODES.filter((n) => n.status === 'online').length,
  edgeNodesTotal: AI_EDGE_NODES.length,
  avgInferenceMs: Math.round(
    AI_MODELS.reduce((s, m) => s + m.inferenceMs, 0) / AI_MODELS.length,
  ),
}
